/**
 * Single-flight refresh coordinator.
 *
 * When several requests fail with 401 in parallel, we want exactly one
 * refresh round-trip. Everyone else awaits its result.
 *
 * The coordinator itself is unaware of *how* refresh is performed —
 * callers pass a thunk. This keeps the coordinator decoupled from the
 * concrete auth API and easy to test in isolation.
 */

/**
 * - `ok` — refresh succeeded, fresh tokens are in place.
 * - `failed` — the server rejected the refresh (expired/revoked refresh
 *   token, CSRF mismatch). The session is genuinely gone → logout.
 * - `unreachable` — the refresh request never got a server verdict
 *   (offline, timeout, or a CORS-blocked preflight on `/auth/refresh`).
 *   The session may well still be valid; this is transient, so the
 *   caller must NOT log the user out — it surfaces the original error
 *   and lets the user retry.
 */
export type RefreshResult = 'ok' | 'failed' | 'unreachable'

export interface RefreshCoordinator {
  /**
   * Trigger refresh if one is not already in flight. Returns the same
   * promise to every concurrent caller until it settles.
   */
  refresh(thunk: () => Promise<RefreshResult>): Promise<RefreshResult>
  /** Whether a refresh is currently in flight. Exposed for tests. */
  isRefreshing(): boolean
}

export function createRefreshCoordinator(): RefreshCoordinator {
  let inflight: Promise<RefreshResult> | null = null

  return {
    refresh(thunk) {
      if (inflight) return inflight
      inflight = (async () => {
        try {
          return await thunk()
        } catch {
          return 'failed' as const
        } finally {
          inflight = null
        }
      })()
      return inflight
    },
    isRefreshing() {
      return inflight !== null
    },
  }
}
